/**
 * Light / dark / system theme.
 *
 * The switch is the `dark` class on `<html>`, never a bare
 * `prefers-color-scheme` media query, because some surfaces must be able to pin
 * a theme: print stylesheets and the server-side PDF renderer (CLAUDE.md §6)
 * always render light regardless of what the operator's laptop prefers.
 *
 * State is module-level so every component and every app shell observes the
 * same value without prop drilling or a store dependency.
 */

import { computed, effectScope, ref, watch, type ComputedRef } from 'vue'

export type ThemeChoice = 'light' | 'dark' | 'system'
export type ResolvedTheme = 'light' | 'dark'

const STORAGE_KEY = 'erpx.theme'
const DARK_CLASS = 'dark'

const choice = ref<ThemeChoice>('system')
const systemPrefersDark = ref(false)

let initialised = false
let mediaQuery: MediaQueryList | null = null

function isThemeChoice(value: string | null): value is ThemeChoice {
  return value === 'light' || value === 'dark' || value === 'system'
}

function readStoredChoice(): ThemeChoice {
  if (typeof localStorage === 'undefined') return 'system'
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return isThemeChoice(stored) ? stored : 'system'
  } catch {
    // Private mode or a blocked storage partition; the default is fine.
    return 'system'
  }
}

function persistChoice(value: ThemeChoice): void {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(STORAGE_KEY, value)
  } catch {
    // Preference is a nicety, not a requirement — losing it is acceptable.
  }
}

function applyToDocument(resolved: ResolvedTheme): void {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  root.classList.toggle(DARK_CLASS, resolved === 'dark')
  // Tells the UA to render scrollbars, form controls and the caret to match.
  root.style.colorScheme = resolved
}

function initialise(): void {
  if (initialised) return
  initialised = true

  choice.value = readStoredChoice()

  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    systemPrefersDark.value = mediaQuery.matches
    const onChange = (event: MediaQueryListEvent): void => {
      systemPrefersDark.value = event.matches
    }
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', onChange)
    }
  }
}

const resolved = computed<ResolvedTheme>(() => {
  if (choice.value === 'system') return systemPrefersDark.value ? 'dark' : 'light'
  return choice.value
})

const isDark = computed(() => resolved.value === 'dark')

/**
 * Detached scope: the DOM sync must outlive whichever component happened to
 * call `useTheme()` first, so it must not be adopted by that component's scope.
 */
let syncScope: ReturnType<typeof effectScope> | null = null

export interface ThemeApi {
  /** What the user asked for, including `system`. */
  theme: ComputedRef<ThemeChoice>
  /** What is actually painted right now. */
  resolved: ComputedRef<ResolvedTheme>
  isDark: ComputedRef<boolean>
  setTheme: (value: ThemeChoice) => void
  /** Flip between light and dark, leaving `system` behind deliberately. */
  toggle: () => void
}

const themeChoice = computed(() => choice.value)

/**
 * Access the shared theme. Safe to call from any component; the first call
 * hydrates from storage, starts watching the OS preference, and syncs the
 * `dark` class onto `<html>`.
 */
export function useTheme(): ThemeApi {
  initialise()

  if (!syncScope) {
    syncScope = effectScope(true)
    syncScope.run(() => {
      watch(resolved, applyToDocument, { immediate: true })
    })
  }

  const setTheme = (value: ThemeChoice): void => {
    choice.value = value
    persistChoice(value)
  }

  return {
    theme: themeChoice,
    resolved,
    isDark,
    setTheme,
    toggle: () => setTheme(resolved.value === 'dark' ? 'light' : 'dark'),
  }
}

/**
 * Inline-able snippet for an app's `index.html` `<head>`. Running it before
 * first paint is what prevents the white flash on a dark-mode reload.
 */
export const THEME_BOOTSTRAP_SCRIPT = `(function(){try{var c=localStorage.getItem('${STORAGE_KEY}');var d=c==='dark'||(c!=='light'&&window.matchMedia('(prefers-color-scheme: dark)').matches);var r=document.documentElement;r.classList.toggle('${DARK_CLASS}',d);r.style.colorScheme=d?'dark':'light';}catch(e){}})();`

/** Test seam: forget the cached preference and OS watcher. */
export function resetTheme(): void {
  initialised = false
  syncScope?.stop()
  syncScope = null
  mediaQuery = null
  choice.value = 'system'
  systemPrefersDark.value = false
}
